import React, { useState, useContext, useEffect, useRef } from "react"
import CheckoutSuccessPopup from "./CheckoutSuccessPopup"
import Loader from "../ui/loader"
import moment from "moment"
import PaymentMethodModal from "../account-settings/payment-methods/PaymentMethodModal"
import PayPalCheckoutServiceComponent from "./PayPalCheckoutServiceComponent"
import PromoCodeInput from "./PromocodeInput"
import useDate from "../../hooks/useDate"
import useLoading from "../../hooks/useLoading"
import useManageServices, { ServiceType } from "./hooks/useManageServices"
import useScrollToTop from "../../hooks/useScrollToTop"
import { Badge } from "../ui/badge"
import { Button } from "../ui/button"
import { ClientPaymentRequests } from "../../api/app.service"
import { DateTime } from "luxon"
import { getPrice } from "../../utils/services/get-price"
import { getUnits } from "../../utils/services/get-units/get-units"
import { formatAndSetDates } from "../../utils/dateFormaterForUI"
import { formatTextWithBreaks } from "../../utils/services/service-content-formatter"
import {
  PaymentRequests,
  ServiceRequests,
  ContactRequests,
  MeetingRequests,
} from "../../api/app.service"
import { pluralize } from "../../utils/pluralize"
import { PublicProfileRequests } from "../../api/public.service"
import {
  SvgBundle,
  SvgCheck,
  SvgCheckCircle,
  SvgConsultation,
  SvgLock2,
  SvgPayment,
  SvgPiechartOneQuarter,
  SvgPiechartThreeQuarters,
  SvgRecurring,
  SvgUserCheck,
  SvgChevronLeft,
  SvgClock,
} from "../icons/"
import { useLocation, useParams } from "react-router-dom"
import { UserCtx } from "../../context/userContext"
import { useToast } from "../../hooks/useToast"
import { useNavigate } from "react-router-dom"

interface Props {
  service?: any
  isGroupService?: any
  availabilities?: any
  meetingDetails: {
    startTime: Date
    endTime: Date
  }

  contact: any
  activeContactRef: any
  selectedPlanFromChild: any
}

const ProgramCheckout: React.FC<Props> = ({
  service,
  isGroupService,
  availabilities,
  contact,
  activeContactRef,
  selectedPlanFromChild,
}) => {
  const [contacts, setContacts] = useState<any>(null)
  const [activeService, setActiveService] = useState<any>(null)
  const [activeContact, setActiveContact] = useState<any>(null)
  const [options, setOptions] = useState<any>({})
  const [meetingDetails, setMeetingDetails] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [merchantId, setMerchantId] = useState<string | null>(null)
  const [checkoutSuccessPopup, setCheckoutSuccessPopup] = useState(false)
  const [spotsLeft, setSpotsLeft] = useState<number | null>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const [hovered, setHovered] = useState(false)
  const { user } = useContext(UserCtx)
  const user_id = parseInt(user.user_id)
  const { toast } = useToast()
  const { startLoading, stopLoading } = useLoading()
  const [paymentMethods, setPaymentMethods] = useState<any[]>([])
  const [paymentMethodModal, setPaymentMethodModal] = useState<boolean>(false)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>({})
  const { getServiceObject } = useManageServices()
  const [discountedOrderTotal, setDiscountedOrderTotal] = useState(0)
  const [promoTotal, setPromoTotal] = useState(0)
  const [discountedUpFrontAmount, setDiscountedUpFrontAmount] = useState(0)
  const [discountInstallment, setDiscountInstallment] = useState(0)
  const [isTermsAgreed, setIsTermsAgreed] = useState<boolean>(false)
  const [addPromoCode, setAddPromoCode] = useState<boolean>(false)
  const [appliedDiscount, setAppliedDiscount] = useState<any>({
    amount: 0,
    promoCode: "",
    promoType: "",
    promoValue: 0,
  })
  const [promoCode, setPromoCode] = useState<string>("")
  const { mdDate } = useDate()
  const [formattedSortedStartTimes, setFormattedSortedStartTimes] = useState<
    string[]
  >([])
  const [activeDate, setActiveDate] = useState<DateTime>(
    DateTime.local()
      .startOf("day")
      .setZone(user?.timezone?.value || "utc")
  )
  const [meetings, setMeetings] = useState<any[]>([])
  const { service_id, endpoint_slug, contact_id } = useParams()
  const [coachPublicProfileData, setCoachPublicProfileData] =
    useState<any>(null)

  const [endpointSlug, setEndpointSlug] = useState<string | null>(
    endpoint_slug || null
  )
  const [coachUserID, setCoachUserID] = useState<string | null>()
  const isDisabled =
    Number(coachPublicProfileData?.user_id) === Number(user?.user_id)

  // Calculate the middle point of the formatted start times (from recurring group services)
  const halfwayIndex = Math.ceil(formattedSortedStartTimes.length / 2)
  // Take the first half from the above function to call to the left side (when called appropriately)
  const leftColumnItems = formattedSortedStartTimes.slice(0, halfwayIndex)
  //  Take the last half of the halfwayIndex function to the right (when called appropriately)
  const rightColumnItems = formattedSortedStartTimes.slice(halfwayIndex)

  const getUserTimezone = () => {
    const localDateTime = DateTime.now().zoneName
    const longTimezoneName = DateTime.now().offsetNameLong
    return `${longTimezoneName} - ${localDateTime}`
  }

  const userTimezone = getUserTimezone()

  const getExpirationDate = () => {
    const { time, type } = activeService?.service_details?.expiration
    const expDate = DateTime.now()
      .plus({ [type]: time })
      .toFormat("MMMM dd, yyyy")
    return expDate
  }

  const getExpirationText = () => {
    let expText: string = ""
    if (
      ["individual", "consultation", "bundle"].includes(
        activeService?.service_details?.type
      )
    ) {
      expText = "Your session(s) must be used no later than"
    } else if (activeService?.service_details?.type === "recurring") {
      expText = "This service will be recurring until"
    }
    return expText
  }

  const getServiceType = () => {
    if (activeService?.service_details?.type === "consultation") {
      return (
        <div className="flex items-center gap-[8px] text-base font-bold">
          <SvgConsultation className="text-blurple" />
          Consultation
        </div>
      )
    } else if (activeService?.service_details?.type === "individual") {
      return (
        <div className="flex items-center gap-[8px] text-base font-bold">
          <SvgUserCheck className="text-blurple" />
          Individual Session
        </div>
      )
    } else if (activeService?.service_details?.type === "bundle") {
      return (
        <div className="flex items-center gap-[8px] text-base font-bold">
          <SvgBundle className="text-blurple" />
          Bundle Package
        </div>
      )
    } else if (activeService?.service_details?.type === "recurring") {
      return (
        <div className="flex items-center gap-[8px] text-base font-bold">
          <SvgRecurring />
          Recurring Program
        </div>
      )
    }
  }

  const getInstallmentAmounts = () => {
    const percent =
      activeService?.service_details?.billing?.upFrontPercentage / 100
    const amount = parseInt(activeService?.service_details?.billing?.amount)
    const upfront = amount * percent
    const installment =
      (amount - upfront) /
      activeService?.service_details?.billing?.installmentIncrements?.length
    return {
      upfront: `$${upfront.toFixed(2)}`,
      installment: `$${installment.toFixed(2)}`,
    }
  }

  const getRecurringTotal = () => {
    const billingAmount = parseFloat(
      activeService?.service_details?.billing?.amount || "0"
    )
    const expirationTime = parseInt(
      activeService?.service_details?.expiration?.time || "1"
    )
    const discountAmount = appliedDiscount?.amount
      ? parseFloat(appliedDiscount.amount)
      : 0

    const totalRecurringCost = billingAmount * expirationTime
    const totalCostAfterDiscount = totalRecurringCost - discountAmount
    const finalTotalCost = Math.max(0, totalCostAfterDiscount)

    return finalTotalCost.toFixed(2)
  }

  const getHeaderText = () => {
    if (activeService?.service_details?.type === "consultation") {
      if (parseInt(activeService?.service_details?.billing?.amount) > 0) {
        return "Consulation Details"
      } else {
        return "Consultation Details"
      }
    } else {
      return "Service Checkout"
    }
  }

  const getDetailsText = () => {
    if (activeService?.service_details?.type === "consultation") {
      return "Coach Details"
    } else {
      return "Meeting Details"
    }
  }

  const calculateTotalAmount = () => {
    let totalAmount = 0

    const amount = parseFloat(
      activeService?.service_details?.billing?.amount || "0"
    )
    const time = parseInt(
      activeService?.service_details?.expiration?.time || "1"
    )

    if (
      activeService?.service_details?.type === "recurring" &&
      appliedDiscount.amount >= 0
    ) {
      totalAmount = amount * time - appliedDiscount?.amount
    } else {
      totalAmount = amount
    }

    if (appliedDiscount) {
      const time = parseInt(
        activeService?.service_details?.expiration?.time || "1"
      )
      const isRecurring = activeService?.service_details?.type === "recurring"

      if (
        appliedDiscount.promoType === "percentage" ||
        appliedDiscount.promoType === "percent"
      ) {
        if (isRecurring) {
          const perCycle = totalAmount / time
          totalAmount = perCycle
        } else {
          totalAmount *= 1 - parseFloat(appliedDiscount.promoValue) / 100
        }
      } else {
        if (isRecurring) {
          totalAmount = totalAmount / time
        } else {
          totalAmount -= parseFloat(appliedDiscount.amount || "0")
        }
      }
    }
    return totalAmount < 0 ? 0 : totalAmount
  }

  const getCurrencySymbol = () => {
    return String.fromCharCode(
      parseInt(activeService?.service_details?.symbol?.slice(2), 16)
    )
  }

  useEffect(() => {
    if (activeService && appliedDiscount) {
      const newUpfrontAmount = calculateUpFront()
      setDiscountedUpFrontAmount(newUpfrontAmount)

      const newInstallmentAmount = calculateDiscountedUpInstallment()
      setDiscountInstallment(newInstallmentAmount)

      const newTotalAmount = calculateTotalAmount()
      setDiscountedOrderTotal(newTotalAmount)
      const promoTotalValue =
        parseFloat(activeService?.service_details?.billing?.amount || "0") -
        newTotalAmount
      setPromoTotal(promoTotalValue)
    }
  }, [appliedDiscount, activeService])

  const calculateUpFront = (): number => {
    const isRecurring = activeService?.service_details?.type === "recurring"
    const discountAmount = parseFloat(appliedDiscount?.promoValue || "0")
    const discountType = appliedDiscount?.promoType

    const amount = parseFloat(
      activeService?.service_details?.billing?.amount || "0"
    )
    const time = parseInt(
      activeService?.service_details?.expiration?.time || "1"
    )
    const totalAmount = isRecurring ? amount * time : amount
    const upFrontPercentage =
      activeService?.service_details?.billing?.upFrontPercentage / 100 || 0

    let upfrontAmount = 0

    if (isRecurring) {
      upfrontAmount = totalAmount
    } else {
      const upfrontAmountStr = getInstallmentAmounts().upfront
      upfrontAmount = parseFloat(upfrontAmountStr.replace("$", "").trim())
    }

    if (
      !activeService?.service_details?.billing?.installmentIncrements ||
      activeService?.service_details?.billing?.installmentIncrements.length ===
        0
    ) {
      return (
        (totalAmount - parseFloat(appliedDiscount?.amount || "0")) /
        activeService?.service_details.expiration.time
      )
    }

    if (!isNaN(upfrontAmount) && !isNaN(discountAmount) && appliedDiscount) {
      if (discountType === "percent") {
        const discountPercentage = discountAmount / 100
        const discountedAmount = upfrontAmount * (1 - discountPercentage)
        return discountedAmount
      } else if (discountType === "amount") {
        const newAmount = (totalAmount - discountAmount) * upFrontPercentage
        return newAmount
      }
    }

    return upfrontAmount
  }

  const calculateDiscountedUpInstallment = () => {
    const installAmountStr = getInstallmentAmounts().installment
    const upfrontAmount = parseFloat(installAmountStr.replace("$", "").trim())
    const discountAmount = parseFloat(appliedDiscount?.promoValue || "0")
    const discountType = appliedDiscount?.promoType
    let totalAmount = parseFloat(
      activeService?.service_details?.billing?.amount
    )
    const discountLength =
      activeService?.service_details.billing.installmentIncrements?.length

    if (!discountLength || isNaN(discountLength) || discountLength <= 0) {
      return upfrontAmount
    }

    if (!isNaN(upfrontAmount) && !isNaN(discountAmount) && appliedDiscount) {
      if (discountType === "percent") {
        const discountPercentage = discountAmount / 100
        const discountedAmount = upfrontAmount * (1 - discountPercentage)
        return discountedAmount
      } else if (discountType === "amount") {
        const adjustedTotal = totalAmount - parseFloat(appliedDiscount.amount)
        const newAmount = (adjustedTotal - calculateUpFront()) / discountLength
        return newAmount
      }
    }

    return upfrontAmount
  }

  const getPaymentDetails = () => {
    switch (activeService?.service_details?.type) {
      case "free":
        break
      case "individual":
        return (
          <>
            <div className="mb-[62px] mt-[24px] flex items-center">
              <div className="flex flex-col">
                <div className="border-grayMist text-blurple flex h-[60px] w-[60px] items-center justify-center rounded-full border">
                  <SvgCheck className="h-4 w-4" />
                </div>
                <p className="min-w-fit text-base">Select coach</p>
              </div>
              <div className="border-grayCloud mx-[24px] mb-[32px] h-[2px] w-full border-b border-dashed" />
              <div className="flex flex-col">
                <div className="border-grayMist text-blurple flex h-[60px] w-[60px] items-center justify-center rounded-full border">
                  <SvgCheck className="h-4 w-4" />
                </div>
                <p className="min-w-fit text-base">Select program</p>
              </div>
              <div className="border-grayCloud mx-[24px] mb-[32px] h-[2px] w-full border-b border-dashed" />
              <div className="flex flex-col">
                <div className="border-blurple text-blurple flex h-[60px] w-[60px] items-center justify-center rounded-full border">
                  <SvgPayment />
                </div>
                <p className="min-w-fit text-base">Checkout</p>
              </div>
            </div>
            {activeService?.service_details?.allow_promo && (
              <PromoCodeInput
                addPromoCode={addPromoCode}
                appliedDiscount={appliedDiscount}
                setAddPromoCode={setAddPromoCode}
                promoCode={promoCode}
                handlePromoCodeChange={handlePromoCodeChange}
                validatePromoCode={validatePromoCode}
                symbol={activeService?.service_details?.symbol}
              />
            )}
            <div className="mb-[8px] flex items-center">
              <p className="min-w-fit text-base">Individual Session</p>
              <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
              <p className="min-w-fit text-base">
                {activeService &&
                  `${String.fromCharCode(
                    parseInt(
                      activeService?.service_details?.symbol?.slice(2),
                      16
                    )
                  )}${parseInt(
                    activeService?.service_details?.billing?.amount
                  ).toFixed(2)}`}
              </p>
            </div>
            <div className="mb-[8px] flex items-center">
              <p className="min-w-fit text-base">Interest or fees</p>
              <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
              {/* TODO make dynamice */}
              <p className="min-w-fit text-base">
                {" "}
                {activeService &&
                  String.fromCharCode(
                    parseInt(
                      activeService?.service_details?.symbol?.slice(2),
                      16
                    )
                  )}
                0.00
              </p>
            </div>
            <div className="mb-[24px] flex items-center">
              <p className="min-w-fit text-[18px] font-bold">Order total</p>
              <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
              {/* TODO make dynamice */}
              <p className="min-w-fit text-base font-bold">
                {getCurrencySymbol()}
                {calculateUpFront().toFixed(2)}
              </p>
            </div>
          </>
        )
      case "consultation":
        return (
          <>
            <div className="mb-[62px] mt-[24px] flex items-center">
              <div className="flex flex-col">
                <div className="border-grayMist text-blurple flex h-[60px] w-[60px] items-center justify-center rounded-full border">
                  <SvgCheck className="h-4 w-4" />
                </div>
                <p className="min-w-fit text-base">Select coach</p>
              </div>
              <div className="border-grayCloud mx-[24px] mb-[32px] h-[2px] w-full border-b border-dashed" />
              <div className="flex flex-col">
                <div className="border-grayMist text-blurple flex h-[60px] w-[60px] items-center justify-center rounded-full border">
                  <SvgCheck className="h-4 w-4" />
                </div>
                <p className="min-w-fit text-base">Select program</p>
              </div>
              <div className="border-grayCloud mx-[24px] mb-[32px] h-[2px] w-full border-b border-dashed" />
              <div className="flex flex-col">
                <div className="border-blurple text-blurple flex h-[60px] w-[60px] items-center justify-center rounded-full border">
                  <SvgPayment />
                </div>
                <p className="min-w-fit text-base">Checkout</p>
              </div>
            </div>
            <>
              {activeService?.service_details?.allow_promo && (
                <PromoCodeInput
                  addPromoCode={addPromoCode}
                  appliedDiscount={appliedDiscount}
                  setAddPromoCode={setAddPromoCode}
                  promoCode={promoCode}
                  handlePromoCodeChange={handlePromoCodeChange}
                  validatePromoCode={validatePromoCode}
                  symbol={activeService?.service_details?.symbol}
                />
              )}
              <div className="mb-[8px] flex items-center">
                <p className="min-w-fit text-base">Consultation Session</p>
                <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                {/* TODO make dynamic */}
                <p className="min-w-fit text-base">
                  {activeService?.service_details &&
                    `${String.fromCharCode(
                      parseInt(
                        activeService?.service_details?.symbol?.slice(2),
                        16
                      )
                    )}${parseInt(
                      activeService?.service_details?.billing?.amount
                    ).toFixed(2)}`}
                </p>
              </div>
              <div className="mb-[8px] flex items-center">
                <p className="min-w-fit text-base">Interest or fees</p>
                <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                {/* TODO make dynamic */}
                <p className="min-w-fit text-base">$0.00</p>
              </div>
              <div className="mb-[24px] flex items-center">
                <p className="min-w-fit text-[18px] font-bold">Order total</p>
                <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                {/* TODO make dynamic */}
                <p className="min-w-fit text-[18px] font-bold">
                  {activeService?.service_details &&
                    `${String.fromCharCode(
                      parseInt(
                        activeService?.service_details?.symbol?.slice(2),
                        16
                      )
                    )}${(
                      parseInt(
                        activeService?.service_details?.billing?.amount
                      ) - parseInt(appliedDiscount.amount)
                    ).toFixed(2)}`}
                </p>
              </div>
            </>
          </>
        )
      case "recurring":
        return (
          <>
            <h5 className="text-graySlate mb-[16px] text-base">
              {activeService?.service_details?.allow_promo && (
                <PromoCodeInput
                  addPromoCode={addPromoCode}
                  appliedDiscount={appliedDiscount}
                  setAddPromoCode={setAddPromoCode}
                  promoCode={promoCode}
                  handlePromoCodeChange={handlePromoCodeChange}
                  validatePromoCode={validatePromoCode}
                  symbol={activeService?.service_details?.symbol}
                />
              )}
              PAYMENT SCHEDULE
            </h5>
            <div className="mb-[14px] flex items-center">
              <SvgPiechartOneQuarter className="mr-[16px]" />
              <p className="min-w-fit text-base">1st payment today</p>
              <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
              {/* TODO make dynamic */}
              <p className="min-w-fit text-base">
                {activeService?.service_details &&
                  (() => {
                    const details = activeService.service_details
                    const symbol = String.fromCharCode(
                      parseInt(details.symbol?.slice(2), 16)
                    )
                    const baseAmount = parseFloat(
                      details.billing?.amount || "0"
                    )
                    const isRecurring = details.type === "recurring"
                    const discountRaw = appliedDiscount?.amount
                      ? parseFloat(appliedDiscount.amount)
                      : 0
                    const cycles =
                      isRecurring && details.expiration?.time
                        ? details.expiration.time
                        : 1
                    const adjustedDiscount = isRecurring
                      ? discountRaw / cycles
                      : discountRaw
                    const finalAmount = (baseAmount - adjustedDiscount).toFixed(
                      2
                    )

                    return `${symbol}${finalAmount}`
                  })()}
              </p>
            </div>
            <div className="mb-[12px] flex items-center">
              <SvgRecurring className="mr-[16px]" />
              <p className="min-w-fit text-base">Recurring payments</p>
              <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
              {/* TODO make dynamic */}
              <p className="min-w-fit text-base">
                {activeService?.service_details &&
                  (() => {
                    const details = activeService.service_details
                    const symbol = String.fromCharCode(
                      parseInt(details.symbol?.slice(2), 16)
                    )
                    const baseAmount = parseFloat(
                      details.billing?.amount || "0"
                    )
                    const isRecurring = details.type === "recurring"
                    const discountRaw = appliedDiscount?.amount
                      ? parseFloat(appliedDiscount.amount)
                      : 0
                    const cycles =
                      isRecurring && details.expiration?.time
                        ? details.expiration.time
                        : 1
                    const adjustedDiscount = isRecurring
                      ? discountRaw / cycles
                      : discountRaw
                    const finalAmount = (baseAmount - adjustedDiscount).toFixed(
                      2
                    )

                    return `${symbol}${finalAmount}`
                  })()}
              </p>
            </div>
            {/* TODO make payment schedule and credit card dynamic */}
            <p className="text-graySlate mb-[24px] text-base">
              Payments will be charged &thinsp;
              <strong className="text-black">
                {activeService?.service_details?.billing.unit === "month"
                  ? "monthly"
                  : "weekly"}
              </strong>{" "}
              &thinsp;on the anniversary of the purchase date to the payment
              method you select below
            </p>
            <div className="bg-grayMist mb-[24px] h-[1px] w-full" />
            <div className="mb-[8px] flex items-center">
              <p className="min-w-fit text-base">Interest or fees</p>
              <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
              {/* TODO make dynamic */}
              <p className="min-w-fit text-base">
                {String.fromCharCode(
                  parseInt(activeService?.service_details?.symbol?.slice(2), 16)
                )}
                0.00
              </p>
            </div>
            <div className="mb-[24px] flex items-center">
              <p className="min-w-fit text-base font-bold">Order total</p>
              <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
              {/* TODO make dynamic */}
              <p className="min-w-fit text-base font-bold">
                {`$${getRecurringTotal()}`}
              </p>
            </div>
            <div className="border-grayCloud flex items-center rounded-lg border-[1px] p-2">
              <p className="min-w-fit text-lg font-extrabold">Due Today</p>
              <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
              <p className="min-w-fit text-[18px] font-bold">
                {activeService?.service_details &&
                  (() => {
                    const details = activeService.service_details
                    const symbol = String.fromCharCode(
                      parseInt(details.symbol?.slice(2), 16)
                    )
                    const baseAmount = parseFloat(
                      details.billing?.amount || "0"
                    )
                    const isRecurring = details.type === "recurring"
                    const discountRaw = appliedDiscount?.amount
                      ? parseFloat(appliedDiscount.amount)
                      : 0
                    const cycles =
                      isRecurring && details.expiration?.time
                        ? details.expiration.time
                        : 1
                    const adjustedDiscount = isRecurring
                      ? discountRaw / cycles
                      : discountRaw
                    const finalAmount = (baseAmount - adjustedDiscount).toFixed(
                      2
                    )

                    return `${symbol}${finalAmount}`
                  })()}
              </p>
            </div>
          </>
        )
      case "bundle":
        if (activeService?.service_details?.billing?.unit === "installment") {
          return (
            <>
              <h5 className="text-graySlate mb-[16px] text-base">
                PAYMENT SCHEDULE
              </h5>
              <div className="mb-[14px] flex items-center">
                <SvgPiechartOneQuarter className="mr-[16px]" />
                <p className="min-w-fit text-base">1st installment today</p>
                <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                {/* TODO make dynamic */}
                <p className="min-w-fit text-base">
                  {getCurrencySymbol()}
                  {calculateUpFront().toFixed(2)}
                </p>
              </div>
              <div className="mb-[12px] flex items-center">
                <SvgPiechartThreeQuarters className="mr-[16px]" />
                <p className="min-w-fit text-base">Future installments</p>
                <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                {/* TODO make dynamic */}
                <p className="min-w-fit text-base">
                  <span className="text-graySlate">
                    {
                      activeService?.service_details?.billing
                        ?.installmentIncrements?.length
                    }
                    x&nbsp;
                  </span>
                  {getCurrencySymbol()}
                  {calculateDiscountedUpInstallment().toFixed(2)}
                </p>
              </div>
              {/* TODO make payment schedule and credit card dynamic */}
              <p className="text-graySlate mb-[24px] text-base">
                Payments will be charged on the installment dates to the payment
                method you select below
              </p>
              <div className="bg-grayMist mb-[24px] h-[1px] w-full" />
              {activeService?.service_details?.allow_promo && (
                <PromoCodeInput
                  addPromoCode={addPromoCode}
                  appliedDiscount={appliedDiscount}
                  setAddPromoCode={setAddPromoCode}
                  promoCode={promoCode}
                  handlePromoCodeChange={handlePromoCodeChange}
                  validatePromoCode={validatePromoCode}
                  symbol={activeService?.service_details?.symbol}
                />
              )}
              <div className="mb-[8px] flex items-center">
                <p className="min-w-fit text-base">Interest or fees</p>
                <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                {/* TODO make dynamic */}
                <p className="min-w-fit text-base">
                  {String.fromCharCode(
                    parseInt(
                      activeService?.service_details?.symbol?.slice(2),
                      16
                    )
                  )}
                  0.00
                </p>
              </div>
              <div className="mb-[24px] flex items-center">
                <p className="min-w-fit text-base font-bold">Order total</p>
                <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                {/* TODO make dynamic */}
                <p className="min-w-fit text-base font-bold">
                  {activeService && (
                    <>
                      {getCurrencySymbol()}
                      {calculateTotalAmount().toFixed(2)}
                    </>
                  )}
                </p>
              </div>
              <div className="border-grayCloud flex items-center rounded-lg border-[1px] p-2">
                <p className="min-w-fit text-lg font-extrabold">Due Today</p>
                <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                <p className="min-w-fit text-[18px] font-bold">
                  {getCurrencySymbol()}
                  {calculateUpFront().toFixed(2)}
                </p>
              </div>
            </>
          )
        } else {
          return (
            <>
              <div className="mb-[62px] mt-[24px] flex items-center">
                <div className="flex flex-col">
                  <div className="border-grayMist flex h-[60px] w-[60px] items-center justify-center rounded-full border">
                    <SvgCheck stroke="#3395FF" />
                  </div>
                  <p className="min-w-fit text-base">Select coach</p>
                </div>
                <div className="border-grayCloud mx-[24px] mb-[32px] h-[2px] w-full border-b border-dashed" />
                <div className="flex flex-col">
                  <div className="border-grayMist flex h-[60px] w-[60px] items-center justify-center rounded-full border">
                    <SvgCheck stroke="#3395FF" />
                  </div>
                  <p className="min-w-fit text-base">Select program</p>
                </div>
                <div className="border-grayCloud mx-[24px] mb-[32px] h-[2px] w-full border-b border-dashed" />
                <div className="flex flex-col">
                  <div className="border-primaryBlue flex h-[60px] w-[60px] items-center justify-center rounded-full border">
                    <SvgCheck stroke="#3395FF" />
                  </div>
                  <p className="min-w-fit text-base">Checkout</p>
                </div>
              </div>
              {activeService?.service_details?.allow_promo && (
                <PromoCodeInput
                  addPromoCode={addPromoCode}
                  appliedDiscount={appliedDiscount}
                  setAddPromoCode={setAddPromoCode}
                  promoCode={promoCode}
                  handlePromoCodeChange={handlePromoCodeChange}
                  validatePromoCode={validatePromoCode}
                  symbol={activeService?.service_details?.symbol}
                />
              )}
              {/* <div className="h-[1px] w-full bg-grayMist mb-[24px]" /> */}
              <div className="mb-[8px] flex items-center">
                <p className="min-w-fit text-base">Session bundle</p>
                <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                {/* TODO make dynamic */}
                <p className="min-w-fit text-base">
                  {activeService &&
                    `${String.fromCharCode(
                      parseInt(
                        activeService?.service_details?.symbol?.slice(2),
                        16
                      )
                    )}${parseInt(
                      activeService?.service_details?.billing?.amount
                    ).toFixed(2)}`}
                </p>
              </div>
              <div className="mb-[8px] flex items-center">
                <p className="min-w-fit text-base">Interest or fees</p>
                <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                {/* TODO make dynamic */}
                <p className="min-w-fit text-base">
                  {activeService &&
                    String.fromCharCode(
                      parseInt(
                        activeService?.service_details?.symbol?.slice(2),
                        16
                      )
                    )}
                  0.00
                </p>
              </div>
              <div className="mb-[24px] flex items-center">
                <p className="min-w-fit text-[18px] font-bold">Order total</p>
                <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                {/* TODO make dynamic */}
                <p className="min-w-fit text-[18px] font-bold">
                  {activeService &&
                    `${String.fromCharCode(
                      parseInt(
                        activeService?.service_details?.symbol?.slice(2),
                        16
                      )
                    )}${(
                      parseInt(
                        activeService?.service_details?.billing?.amount
                      ) - parseInt(appliedDiscount.amount)
                    ).toFixed(2)}`}
                </p>
              </div>
            </>
          )
        }
      default:
        break
    }
  }

  const [serviceType, setServiceType] = useState<ServiceType>(
    isGroupService ? "individual" : "consultation"
  )
  const [serviceValues, setServiceValues] = useState<any>(null)
  const determineServiceType = (service: any): ServiceType => {
    if (activeService?.service_details?.type === "recurring") return "recurring"
    if (activeService?.service_details?.type === "group") return "individual"
    if (activeService?.service_details?.type === "individual")
      return "individual"
    if (activeService?.service_details?.type === "bundle") return "bundle"
    return "consultation"
  }

  useEffect(() => {
    setServiceType(determineServiceType(activeService))
    setServiceValues({
      availability_rule_id: availabilities
        ? availabilities?.find((rule: any) => rule?.is_default).id
        : "",
      isGroupService: activeService?.is_group_service,
      color: activeService?.service_details?.image?.color,
      title: activeService?.service_details?.title,
      description: "",
      feature1: activeService?.service_details?.details?.features?.feature1,
      feature2: activeService?.service_details?.details?.features?.feature2,
      feature3: activeService?.service_details?.details?.features?.feature3,
      sessionLength: {
        minutes: activeService?.service_details?.session?.duration,
      },
      sessionCost: activeService?.service_details?.billing.amount,
      currency: activeService?.service_details?.symbol,
      symbol: activeService?.service_details?.symbol,
      isFree: activeService?.service_details?.isFree,
      allowPromo: activeService?.service_details?.allowPromo,
      package: {
        packageSessions: activeService?.service_details?.session?.count,
        packageBillingType:
          activeService?.service_details?.billing?.packageBillingType !==
          undefined
            ? activeService?.service_details?.billing?.packageBillingType
            : activeService?.service_details?.billing?.unit,
        packageCost: activeService?.service_details?.billing.amount,
        installmentIncrements:
          activeService?.service_details?.billing.installmentIncrements,
        upFrontPercentage:
          activeService?.service_details?.billing?.upFrontPercentage,
      },
      recurring: {
        periodCost: activeService?.service_details?.billing.amount,
        recurringFrequency: activeService?.service_details?.billing?.unit,
        sessionsPerPeriod: activeService?.service_details?.session?.count,
        unlimited: false,
      },
      expiration: {
        expireTime: activeService?.service_details?.expiration.time,
        expireType: activeService?.service_details?.expiration.type,
        expires: activeService?.service_details?.expiration?.expires,
      },
      enabled: activeService?.service_details?.enabled,
      profileServiceVisible:
        activeService?.service_details?.visibility?.profile.service,
      profileCostVisible:
        activeService?.service_details?.visibility?.profile.cost,
      marketplaceServiceVisible:
        activeService?.service_details?.visibility?.marketplace.service,
      marketplaceCostVisible:
        activeService?.service_details?.visibility?.marketplace.service,
      image: {
        exists: activeService?.service_details?.image.exists,
        base64: "",
        base64extension: "",
      },
      group: {
        isFree: activeService?.service_details?.isFree,
        weeklyDayTimes: activeService?.service_details?.group?.weeklyDayTimes,
        maximumClients: activeService?.service_details?.maximumClients,
        groupChat: activeService?.service_details?.groupChat,
      },
      serviceLinkedForms: activeService?.service_details?.serviceLinkedForms,
    })
  }, [activeService])

  useEffect(() => {
    if (activeService?.service_details?.billing?.amount) {
      const rawAmount = String(activeService.service_details.billing.amount)
      const sanitizedAmount = rawAmount.replace(/[^0-9.]/g, "")
      const parsedAmount = parseFloat(sanitizedAmount)
      if (!isNaN(parsedAmount)) {
        setServiceValues((prevValues: any) => ({
          ...prevValues,
          sessionCost: parsedAmount,
        }))
      } else {
        console.error("Invalid amount:", rawAmount)
      }
    }
  }, [activeService])

  const getGroupServiceEnrollment = async () => {
    try {
      const response = await ServiceRequests.checkGroupServiceEnrollment({
        stripe_product_id: activeService?.stripe_product_id,
      })
      const data = response
      const spotsLeft = data.spots_left
      setSpotsLeft(spotsLeft)
      return spotsLeft
    } catch (error) {
      console.error("Error fetching group service enrollment:", error)
      return null
    }
  }

  const createFreeOrder = async (data: any) => {
    try {
      if (activeService?.service_details?.isGroupService) {
        if (matchingMeeting) {
          alert("You are already enrolled in this service.")
          window.location.href = `${process.env.REACT_APP_BASE_URL}/providers/${activeContact?.endpoint_slug}/services`
          return
        }

        const spotsLeft = await getGroupServiceEnrollment()
        if (spotsLeft !== null && spotsLeft <= 0) {
          alert(
            "This group service is full. Please choose a different service."
          )
          window.location.href = `${process.env.REACT_APP_BASE_URL}/providers/${activeContact?.endpoint_slug}/services`
          return
        }
      }

      const serviceObject = getServiceObject(serviceType, serviceValues)

      const response = await ServiceRequests.purchaseFreeService({
        user_id,
        coach_user_id: activeContact?.user_id,
        client_profile_id: user.activeProfileId,
        coach_profile_id: activeContact?.profile_id,
        payment_method_id: null,
        service: { ...serviceObject, sessionCost: serviceValues.sessionCost },
        stripe_product_id: activeService?.stripe_product_id,
        user_environment: "",
        promo_code: promoCode,
      })

      if (meetingDetails?.startTime && meetingDetails?.endTime) {
        createMeeting(response.service_usage_id)
      } else if (activeService?.service_details?.isGroupService) {
        MeetingRequests.updateGroupMeeting({
          profile_id: user.activeProfileId,
          user_id: parseInt(user.user_id),
          coach_user_id: activeContact?.user_id,
          group_service_id: activeService.group_service_id,
        })
      }

      setCheckoutSuccessPopup(true)
    } catch (error) {
      console.error("Error in createFreeOrder:", error)

      if (error instanceof Error) {
        if (error.message === "You are already enrolled in this service.") {
          alert(error.message)
          return
        }

        if (error.message === "Group Service Full") {
          alert(
            "This group service is full. Please choose a different service."
          )
        }
      } else {
        console.error("Unknown error:", error)
      }
    } finally {
      setLoading(false)
    }
  }

  const handlePromoCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPromoCode(e.target.value)
  }

  const validatePromoCode = () => {
    if (promoCode !== "") {
      ServiceRequests.validateServicePromoCode({
        promo_code: promoCode,
        coach_id: parseInt(activeContact.user_id),
      })
        .then((data) => {
          if (!data.promo_valid) {
            setAppliedDiscount({
              amount: 0,
              promoCode: "",
              promoType: "",
              promoValue: 0,
            })
            setAddPromoCode(false)
            setPromoCode("")
            toast.error(data.promo_invalid_reason)
          } else {
            const details = data.promo_code
            const discountType = details.promo_type
            const discountUnits = details.promo_value
            const promoCode = details.promo_code
            if (discountType === "percent") {
              const baseAmount =
                activeService?.service_details?.billing?.amount || 0
              const cycles =
                activeService?.service_details?.type === "recurring"
                  ? activeService?.service_details?.expiration?.time || 1
                  : 1

              const discountDollars =
                (discountUnits / 100) * baseAmount * cycles

              setAppliedDiscount({
                amount: discountDollars,
                promoCode: promoCode,
                promoType: discountType,
                promoValue: discountUnits,
              })

              toast.success(
                `Congrats! Your promotion code is saving you ${String.fromCharCode(
                  parseInt(activeService?.service_details?.symbol?.slice(2), 16)
                )}${discountDollars} on this payment`
              )
            } else if (discountType === "amount") {
              setAppliedDiscount({
                amount: discountUnits,
                promoCode: promoCode,
                promoType: discountType,
                promoValue: discountUnits,
              })
              toast.success(
                `Congrats! Your promotion code is saving you ${String.fromCharCode(
                  parseInt(activeService?.service_details?.symbol?.slice(2), 16)
                )}${discountUnits} on this payment`
              )
            }
            setAddPromoCode(false)
          }
        })
        .catch((err) => {
          console.error(err)
          toast.error(err.response.data.message)
        })
    }
  }

  const getMemberName = (member: any) => {
    let name: any = null
    if (member?.firstName) {
      if (member?.lastName) {
        name = `${member?.firstName} ${member?.lastName}`
      } else {
        name = member?.firstName
      }
    }
    return name
  }

  const generateMeetingName = (
    memberParticipant: any,
    coachParticipant: any
  ) => {
    const serviceDetails = activeService?.service_details
    const meetingName = `${coachParticipant?.contact_name} <> ${
      getMemberName(memberParticipant) || memberParticipant?.email
    }${serviceDetails ? ` // ${serviceDetails.title}` : ""}`
    return meetingName
  }

  const createMeeting = async (service_usage_id: any) => {
    const coachProfileId = location.pathname.split("/")[3]
    const coachParticipant: any = contacts?.filter(
      (contact: any) => contact.profile_id === parseInt(coachProfileId)
    )[0]
    const memberParticipant = {
      user_id: parseInt(user?.user_id),
      profile_id: user.activeProfileId,
      email: user?.email,
      contact_name: user?.firstName
        ? `${user?.firstName} ${user?.lastName ? user?.lastName : ""}`
        : "",
      first_name: user?.firstName ? user.firstName : "",
      last_name: user?.lastName ? user.lastName : "",
    }

    const meetingName = generateMeetingName(memberParticipant, coachParticipant)
    const parsedStartTime = meetingDetails?.startTime?.includes(".")
      ? meetingDetails?.startTime.split(".")[0] + "Z"
      : meetingDetails?.startTime

    const parsedEndTime = meetingDetails?.endTime?.includes(".")
      ? meetingDetails?.endTime?.split(".")[0] + "Z"
      : meetingDetails?.endTime
    MeetingRequests.createMeeting({
      user_id: parseInt(user?.user_id),
      coach_user_id: parseInt(coachParticipant?.user_id),
      profile_id: user.activeProfileId,
      meeting_details: {
        name: meetingName,
        meeting_type: "individual",
        start_time: parsedStartTime,
        end_time: parsedEndTime,
        medium: "zoee_video",
        status: "busy",
        reminders: [],
        participants: [memberParticipant, coachParticipant],
        service_usage_id: parseInt(service_usage_id),
      },
    })
      .then(() => {})
      .catch((ex) => {
        toast.error(ex.response.data.message)
      })
      .finally(() => {
        stopLoading()
      })
  }

  useEffect(() => {
    getPaypalOnboardingStatus()
  }, [activeContact])

  const getPaypalOnboardingStatus = async () => {
    try {
      const response = await ClientPaymentRequests.getPaypalOnboardingStatus({
        seller_payer_id: null,
        seller_user_id: parseInt(activeContact.user_id),
      })
      const merchantId = response?.data?.merchant_id
      setMerchantId(merchantId)
      return merchantId
    } catch (error) {}
  }

  const getPaymentMethods = async () => {
    if (user) {
      try {
        startLoading()
        const data = await PaymentRequests.getSavedPaymentMethods({
          user_id: parseInt(user.user_id),
        })
        setPaymentMethods(data?.data?.payment_tokens || [])
        if (data?.payment_methods?.length > 0) {
          setSelectedPaymentMethod(data?.data?.payment_tokens[0].id)
        } else {
          setSelectedPaymentMethod({})
        }
      } catch (ex: any) {
        console.error("Error fetching or updating payment methods:", ex)
        toast.error(
          ex.response?.data?.message || "Failed to fetch payment methods"
        )
      } finally {
        stopLoading()
      }
    }
  }

  const getSessionCount = () => {
    if (
      ["consultation", "individual"].includes(
        activeService?.service_details?.type
      )
    ) {
      return `${activeService?.service_details?.session?.count} session`
    } else if (["bundle"].includes(activeService?.service_details?.type)) {
      return pluralize(
        activeService?.service_details?.session?.count,
        "session"
      )
    } else if (["recurring"].includes(activeService?.service_details?.type)) {
      return (
        <div>
          {pluralize(activeService?.service_details?.session?.count, "session")}
          <span className="text-darkGray">
            {" "}
            / {`${activeService?.service_details?.billing?.unit}`}
          </span>
        </div>
      )
    } else {
      return "Not Found"
    }
  }

  const getLengthOrExpiration = () => {
    let expirationString = "No expiration"
    if (
      ["consultation", "individual", "bundle"].includes(
        activeService?.service_details?.type
      )
    ) {
      if (activeService?.service_details?.expiration?.expires) {
        expirationString = `Expires after ${activeService?.service_details?.expiration?.time} ${activeService?.service_details?.expiration?.type}`
      } else {
        expirationString = "No expiration"
      }
    } else if (["recurring"].includes(activeService?.service_details?.type)) {
      const units =
        activeService?.service_details?.expiration?.type === "weeks"
          ? "week"
          : "month"
      expirationString = `${activeService?.service_details?.expiration?.time} ${units} program`
    } else {
      expirationString = "Expiration not found"
    }
    return expirationString
  }

  const getContact = async () => {
    startLoading()
    await ContactRequests.getContactList({
      profile_id: user.activeProfileId,
      profile_type: user.activeProfile,
    })
      .then((data: any) => {
        const foundContact = data.contact_list?.find(
          (contact: any) => contact.endpoint_slug === endpoint_slug
        )

        setEndpointSlug(foundContact.endpoint_slug)
        setCoachUserID(foundContact.user_id)
        setContacts(data.contact_list)
        setActiveContact(foundContact)
      })
      .catch((ex) => {
        console.error(ex)
      })
      .finally(() => {
        stopLoading()
      })
  }

  const getService = async () => {
    if (location?.state?.service) {
      setActiveService(location.state.service)
      return
    }

    const coachProfileId =
      coachPublicProfileData?.coach_profile_id || parseInt(contact_id || "", 10)

    if (!coachProfileId || !service_id) {
      console.warn("Missing coachProfileId or service_id")
      return
    }

    startLoading()

    try {
      const data = await ServiceRequests.getServiceListClient({
        profile_id: coachProfileId,
        profile_type: "coach",
      })

      const serviceIdToMatch = parseInt(service_id, 10)

      const foundService = data.service_list.find(
        (service: any) => service.service_id === serviceIdToMatch
      )

      setActiveService(foundService)
    } catch (ex: any) {
      console.error(ex)
      toast.error(ex.response?.data?.message || "Error fetching service list")
    } finally {
      stopLoading()
    }
  }

  const getMeetings = () => {
    if (user) {
      const startOfActiveDate = activeDate
        .startOf("day")
        .setZone(user?.timezone?.value || "utc")
        .toISO()!
      const endOfSixMonths = activeDate
        .plus({ months: 6 })
        .endOf("day")
        .setZone(user?.timezone?.value || "utc")
        .toISO()!

      MeetingRequests.getMeetings({
        user_id: parseInt(user.user_id),
        profile_id: user.activeProfileId,
        from_date: startOfActiveDate,
        to_date: endOfSixMonths,
      })
        .then((data) => {
          if (data && Array.isArray(data.meetings)) {
            setMeetings(data.meetings)
          } else {
            console.error("Invalid meetings data format", data)
            setMeetings([])
          }
        })
        .catch((ex) => {
          toast.error(ex.response?.data?.message || "An error occurred")
        })
    }
  }
  //  too much going on here - refactor - split out into two
  // first one here
  useEffect(() => {
    const meetingFromStorage = localStorage.getItem("meetingToSchedule")
    if (meetingFromStorage) {
      try {
        const parsedMeeting = JSON.parse(meetingFromStorage)
        setMeetingDetails(parsedMeeting)
      } catch (error) {
        console.error("Failed to parse meeting from storage", error)
      }
    }
  }, [])
  // second

  useEffect(() => {
    const getPaymentForContactService = async () => {
      if ((!coachPublicProfileData || !contact_id) && !service_id) {
        return
      }
      try {
        await getService()
        await Promise.all([getContact(), getPaymentMethods()])
      } catch (error) {
        console.error(
          "Error in getPaymentForContactService fxn sequence",
          error
        )
      }
    }
    getPaymentForContactService()
  }, [coachPublicProfileData, contact_id, service_id])

  useEffect(() => {
    getMeetings()
    getContact()
  }, [])

  const getRecurringEvents = async (
    userId: number,
    productId: number,
    dateSetter: (values: string[]) => void
  ) => {
    try {
      const response = await MeetingRequests.getRecurringMeetings({
        user_id: userId,
        stripe_connect_product_id: productId,
      })
      const times = (response.meetings || []).map((m) => m.start_time)
      formatAndSetDates(times, dateSetter)
    } catch (error) {
      console.log("Failed to get recurring meetings", error)
    }
  }

  //  Refactor - remove the formatter first, also find better date handler! date-fn? Luxon? currently both are used in our code. Pick one and let it rule the school!
  useEffect(() => {
    if (activeService?.service_details.type === "recurring") {
      // recurring group service scheduled dates mapping
      getRecurringEvents(
        coachPublicProfileData?.user_id || coachUserID,
        activeService?.stripe_product_id,
        setFormattedSortedStartTimes
      )
    } else if (activeService?.service_details?.group?.meetingDates) {
      // non‑recurring group service scheduled dates mapping
      formatAndSetDates(
        activeService.service_details.group.meetingDates,
        setFormattedSortedStartTimes
      )
    } else {
      // nothing to show
      setFormattedSortedStartTimes([])
    }
  }, [
    activeService?.service_details.type,
    activeService?.service_details?.group?.meetingDates,
    coachPublicProfileData?.user_id,
    activeService?.stripe_product_id,
  ])

  const matchingMeeting = meetings.find(
    (meeting) =>
      meeting.service_details?.title === activeService?.service_details.title
  )

  const getCoachPublicProfile = () => {
    if (endpoint_slug || endpointSlug) {
      startLoading()
      PublicProfileRequests.getCoachPublicProfile({
        endpoint_slug: (endpoint_slug || endpointSlug)!,
      })
        .then((data) => {
          if (data.coach_public_profile) {
            setCoachPublicProfileData(data.coach_public_profile)
          } else {
            setCoachPublicProfileData(null)
          }
        })
        .catch((ex) => {
          console.error(ex)
          toast.error(ex.response.data.message)
          stopLoading()
        })
    }
  }

  useEffect(() => {
    getCoachPublicProfile()
  }, [])

  useScrollToTop()
  const coachName =
    coachPublicProfileData?.first_name || coachPublicProfileData?.last_name
      ? `${coachPublicProfileData?.first_name || ""} ${
          coachPublicProfileData?.last_name || ""
        }`.trim()
      : activeContact?.contact_name || ""

  return (
    <>
      {checkoutSuccessPopup && (
        <CheckoutSuccessPopup
          checkoutSuccessPopup={checkoutSuccessPopup}
          setCheckoutSuccessPopup={setCheckoutSuccessPopup}
          contact={activeContact}
          meetingDetails={meetingDetails}
          activeContact={activeContact}
          activeService={activeService}
        />
      )}
      {paymentMethodModal && options && (
        <PaymentMethodModal
          options={options}
          callback={(data: boolean) => {
            getPaymentMethods()
            setPaymentMethodModal(data)
          }}
          getPaymentMethods={getPaymentMethods}
        />
      )}
      <div className="mx-auto w-full pb-24 md:max-w-screen-xl p-4">
        <Button
          variant="ghost"
          onClick={() => navigate(`/providers/${endpointSlug}/services`)}
        >
          {" "}
          <SvgChevronLeft className="md:rounded-full md:border md:border-grayCloud h-6 w-6 md:h-8 md:w-8 md:p-1 md:group-hover:bg-grayCloud transition-colors" />
          <span className="md:inline">
            back to <span className="text-black">Services & Programs</span>
          </span>
        </Button>
        <div className="mx-auto flex-col md:max-w-screen-xl">
          <h2 className="mb-[34px] mt-0 hidden text-[36px] font-bold md:block">
            {getHeaderText()}
          </h2>

          <div className="relative mt-[30px] flex w-full flex-col justify-between gap-[32px] rounded-[16px] md:flex-row md:items-start">
            <div className="flex w-full flex-col md:w-[60%]">
              <div
                className={`aspect-w-3 aspect-h-1 md:aspect-h-2 mx-auto mb-[32px] w-full rounded-[14px] ${activeService?.service_details?.image?.color} relative min-h-[140px]`}
              >
                {activeService?.service_details?.image?.exists && (
                  <img
                    src={`https://clientservice-images-${
                      process.env.REACT_APP_USER_ENVIRONMENT
                    }.s3.amazonaws.com/${
                      activeService.stripe_product_id
                    }?${new Date().getTime()}`}
                    alt="service"
                    className="aspect-[3/1] w-full rounded-[14px] object-cover"
                  />
                )}
              </div>
              <div className="mx-auto flex w-full flex-col px-[2px]">
                {/* TITLE AND PRICE */}
                <div className="mb-[20px] flex w-full items-center justify-between md:mb-[24px]">
                  <h3 className="text-[18px] font-bold truncate max-w-[500px]">
                    {activeService?.service_details?.title}
                  </h3>
                  <h3 className="text-blurple text-[22px] font-bold">
                    {getPrice(
                      parseInt(activeService?.service_details?.billing?.amount),
                      activeService?.service_details?.symbol
                    )}
                    <span className="text-graySlate text-base font-normal">
                      {getUnits(
                        activeService?.service_details?.billing?.unit,
                        parseInt(
                          activeService?.service_details?.billing?.amount
                        )
                      )}
                    </span>
                  </h3>
                </div>
                <div className="bg-grayMist mb-[16px] h-[1px] w-full"></div>
                <div className="relative mb-[32px] mr-auto flex w-full flex-col gap-[12px] text-base">
                  <div className="absolute right-0 top-0">
                    {getServiceType()}
                  </div>
                  {activeService?.service_details?.type !== "consultation" && (
                    <>
                      <div className="flex items-center gap-[12px]">
                        <SvgCheckCircle />
                        {getSessionCount()}
                      </div>
                      <div className="flex items-center gap-[12px]">
                        <SvgCheckCircle />
                        <div>
                          {parseInt(
                            activeService?.service_details?.session?.duration
                          ) / 60}{" "}
                          minutes
                          <span className="text-darkGray"> / session</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-[12px]">
                        <SvgCheckCircle />
                        {getLengthOrExpiration()}
                      </div>
                    </>
                  )}
                </div>
                <div className="bg-grayFlash mb-4 flex h-[32px] w-[142px] items-center justify-center rounded-[8px] font-bold">
                  Program Details
                </div>
                {activeService?.service_details?.details?.features
                  ?.feature1 && (
                  <div className="mb-[24px] mr-auto flex flex-col gap-[16px] text-base md:mb-[32px]">
                    <div className="flex items-center gap-[12px]">
                      <SvgCheckCircle />
                      {
                        activeService?.service_details?.details.features
                          .feature1
                      }
                    </div>
                    {activeService?.service_details?.details?.features
                      ?.feature2 && (
                      <div className="flex items-center gap-[12px]">
                        <SvgCheckCircle />
                        {
                          activeService?.service_details?.details.features
                            .feature2
                        }
                      </div>
                    )}
                    {activeService?.service_details?.details?.features
                      ?.feature3 && (
                      <div className="flex items-center gap-[12px]">
                        <SvgCheckCircle />
                        {
                          activeService?.service_details?.details.features
                            .feature3
                        }
                      </div>
                    )}
                  </div>
                )}
                {activeService?.service_details?.details?.description1 && (
                  <div className="mb-[40px] space-y-4">
                    {formatTextWithBreaks(
                      activeService?.service_details?.details?.description1
                    )}
                  </div>
                )}
                {activeService?.is_group_service && (
                  <div className="mr-auto w-full mb-4">
                    <div className="mb-4 bg-grayFlash flex h-8 w-[142px] items-center justify-center rounded-[8px] font-bold gap-2">
                      Scheduled Dates
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex flex-col gap-2">
                        {leftColumnItems.map((timeStr, idx) => (
                          <Badge
                            key={`left-${idx}`}
                            variant="neutral"
                            className="w-auto"
                          >
                            {timeStr}
                          </Badge>
                        ))}
                      </div>

                      <div className="flex flex-col items-center gap-2">
                        {rightColumnItems.map((timeStr, idx) => (
                          <Badge
                            key={`right-${idx}`}
                            variant="neutral"
                            className="w-auto"
                          >
                            {timeStr}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="border-grayCloud flex flex-col border-t px-[20px] pt-[32px] md:w-[416px] md:border-l md:border-t-0 md:pl-[36px] md:pr-0 md:pt-0">
              <div className="mb-[24px] flex flex-col gap-[12px]">
                <h3 className="mb-[12px] text-[22px] font-bold">
                  {getDetailsText()}
                </h3>
                <div className="promotion-shadow relative flex flex-col rounded-[16px] bg-white px-[24px] pb-[24px] pt-[28px] md:px-[40px] md:py-[32px]">
                  <h5 className="text-grayCharcoal mb-[16px] text-base font-bold">
                    COACH
                  </h5>
                  <div className="flex w-full items-center gap-[12px]">
                    {/* <ContactAvatar
                      contact={activeContact}
                      width={40}
                      height={40}
                      border={""}
                    /> */}
                    <div className="text-base font-bold">{coachName}</div>
                  </div>
                </div>
                {meetingDetails && (
                  <div className="bg-grayMist relative rounded-[16px] px-[24px] pb-[24px] pt-[28px] md:px-[40px] md:py-[32px]">
                    <h5 className="text-grayCharcoal mb-[16px] text-base font-bold">
                      WHEN
                    </h5>
                    <div>
                      {meetingDetails.startTime ? (
                        <>
                          <p className="mb-[8px] text-[16px] font-bold">
                            {`${mdDate(
                              meetingDetails.startTime
                            )}, ${userTimezone}`}
                          </p>
                          <div className="text-graySlate flex gap-[4px] text-base">
                            <p className="font-bold text-black">
                              &nbsp;
                              {moment(meetingDetails.startTime).format(
                                "h:mm A"
                              )}
                            </p>
                            -
                            <p className="font-bold text-black">
                              &nbsp;
                              {moment(meetingDetails.endTime).format("h:mm A")}
                            </p>
                          </div>
                        </>
                      ) : (
                        <p className="text-vividOrange text-base">
                          No date selected
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* SHOW PAYMENT METHOD SELECTOR IF PAID SERVICE */}
              {parseInt(activeService?.service_details?.billing?.amount) > 0 ? (
                <>
                  <h3 className="mb-[12px] text-[22px] font-bold">
                    Payment Details
                  </h3>
                  {getPaymentDetails()}
                  <div
                    className={`mb-[24px] h-[1px] w-full ${
                      activeService?.service_details?.type === "recurring" ||
                      activeService?.service_details?.type === "bundle"
                        ? ""
                        : "bg-grayMist"
                    }`}
                  />
                  <h5 className="text-graySlate mb-[8px] text-base">
                    EXPIRATION
                  </h5>
                  {/* TODO make expiration date dynamic */}
                  <p className="text-graySlate mb-[24px] text-base">
                    {activeService?.service_details?.expiration?.expires ? (
                      <>
                        {getExpirationText()}&nbsp;
                        <span className="text-mainBlack">
                          {getExpirationDate()}
                        </span>
                      </>
                    ) : (
                      <>This service has no expiration</>
                    )}
                  </p>
                  <div className="bg-grayMist mb-[24px] h-[1px] w-full" />
                  {discountedOrderTotal > 0 && (
                    <div className="mb-[24px] flex gap-[16px]">
                      <div className="text-graySlate text-base">
                        By continuing to enroll, I have read and agree to
                        the&nbsp;
                        <a
                          href="https://zoee.com/terms-and-conditions"
                          className="text-blurple"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Agreement &amp; Disclosures
                        </a>
                        &nbsp;and&nbsp;
                        <a
                          href="https://intercom.help/zoee/en/articles/8623117-how-does-zoee-protect-your-information-on-paypal"
                          className="text-blurple"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Payment Authorization&nbsp;
                        </a>
                        provided by Zoee
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <></>
              )}
              {/* NEED  TO ADD VENMO TO SCRIPT EVENTUALLY */}
              {discountedOrderTotal > 0 ? (
                <>
                  <PayPalCheckoutServiceComponent
                    appliedDiscount={appliedDiscount}
                    isTermsAgreed={isTermsAgreed}
                    setIsTermsAgreed={setIsTermsAgreed}
                    service={service}
                    isGroupService={isGroupService}
                    availabilities={availabilities}
                    activeContact={activeContact}
                    activeService={activeService}
                    paymentMethods={paymentMethods}
                    selectedPaymentMethod={selectedPaymentMethod}
                    setSelectedPaymentMethod={setSelectedPaymentMethod}
                    merchantId={merchantId}
                    setMerchantId={setMerchantId}
                    checkoutSuccessPopup={checkoutSuccessPopup}
                    contact={activeContact}
                    meetingDetails={meetingDetails}
                    setCheckoutSuccessPopup={setCheckoutSuccessPopup}
                    activeContactRef={activeContactRef}
                    selectedPlanFromChild={selectedPlanFromChild}
                    validatePromoCode={validatePromoCode}
                    createMeeting={createMeeting}
                    discountedUpFrontAmount={discountedUpFrontAmount}
                    discountInstallment={discountInstallment}
                    discountedOrderTotal={discountedOrderTotal}
                    promoTotal={promoTotal}
                  />
                  <div className="text-muted-foreground mx-auto flex items-center gap-2 text-base">
                    <SvgLock2 className="h-4 w-4" />
                    <p>Guaranteed safe &amp; secure payments</p>
                  </div>
                </>
              ) : (
                <div
                  style={{ position: "relative", display: "inline-block" }}
                  onMouseEnter={() => setHovered(true)}
                  onMouseLeave={() => setHovered(false)}
                >
                  {activeService?.availability_status === "inactive" && (
                    <div className="w-full text-center mt-2 mb-2">
                      <Badge
                        variant="alert"
                        className="flex justify-center items-center flex-wrap whitespace-normal break-words"
                      >
                        <SvgClock className="shrink-0" />
                        <span className="ml-2">
                          Self scheduling disabled by coach
                        </span>
                      </Badge>
                    </div>
                  )}
                  <Button
                    onClick={() => {
                      setLoading(true)
                      createFreeOrder({})
                    }}
                    disabled={isDisabled}
                    className="w-full"
                  >
                    {loading ? <Loader /> : "Enroll in Service"}
                  </Button>

                  {isDisabled && hovered && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        padding: "6px 12px",
                        backgroundColor: "rgba(0,0,0,0.75)",
                        color: "white",
                        borderRadius: "4px",
                        whiteSpace: "nowrap",
                        fontSize: "12px",
                        zIndex: 10,
                        pointerEvents: "none",
                        userSelect: "none",
                      }}
                    >
                      You cannot enroll in your own service
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ProgramCheckout
